import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** A screen's hold on quiet mode; see LoadingService.beginQuietScope. */
export interface QuietScope {
  /** Call once the screen has rendered and started its start-up requests. */
  settle(): void;
  /** Ends the scope at once (call on destroy). */
  close(): void;
}

interface ScopeState {
  settled: boolean;
  timer: ReturnType<typeof setTimeout>;
}

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  loadingSub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /**
   * Contains in-progress loading requests
   */
  loadingMap: Map<string, boolean> = new Map<string, boolean>();

  /* Quiet mode: requests started while a quiet scope is open don't show the
     global loader. A modal that is usable as soon as it opens holds a scope
     while it fills its dropdowns, so the overlay doesn't cover a form the
     user can already work in.

     A scope stays open until the screen has settled (rendered and started
     its start-up requests) and every quiet request has finished. Follow-up
     requests started from a response (profile, then its country, then that
     country's states) begin before the earlier request finishes, so they are
     quiet too. maxMs is a hard cap, so the loader can never stay off. */
  private scopes = new Set<ScopeState>();
  private quietInFlight = 0;

  constructor() { }

  public get isQuiet(): boolean {
    return this.scopes.size > 0;
  }

  public beginQuietScope(maxMs = 15000): QuietScope {
    const scope: ScopeState = {
      settled: false,
      timer: setTimeout(() => this.endScope(scope), maxMs),
    };
    this.scopes.add(scope);
    return {
      settle: () => {
        scope.settled = true;
        this.endSettledScopes();
      },
      close: () => this.endScope(scope),
    };
  }

  /** Called by the interceptor around each request started while quiet. */
  public quietRequestStarted(): void {
    this.quietInFlight++;
  }

  public quietRequestEnded(): void {
    this.quietInFlight = Math.max(0, this.quietInFlight - 1);
    // Next turn, so a request started from this response's handlers counts.
    setTimeout(() => this.endSettledScopes());
  }

  private endSettledScopes(): void {
    if (this.quietInFlight > 0) return;
    this.scopes.forEach((scope) => {
      if (scope.settled) this.endScope(scope);
    });
  }

  private endScope(scope: ScopeState): void {
    clearTimeout(scope.timer);
    this.scopes.delete(scope);
  }

  setLoading(loading: boolean, url: string): void {
    if (!url) {
      throw new Error('The request URL must be provided to the LoadingService.setLoading function');
    }
    if (loading === true) {
      this.loadingMap.set(url, loading);
      this.loadingSub.next(true);
    } else if (loading === false && this.loadingMap.has(url)) {
      this.loadingMap.delete(url);
    }
    if (this.loadingMap.size === 0) {
      this.loadingSub.next(false);
    }
  }
}
